import type { CSSProperties } from 'react';
import { Eye, X } from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';
import { ROLE_LABEL, type Role } from '../../types';

const ROLE_OPTIONS: Role[] = ['supervisor', 'studioleiter', 'servicekraft', 'callcenter_agent', 'verkaeufer'];

// Admin-Vorschau „Ansicht als …": zeigt Menü, Kundenakte und die Bereiche
// Team/Admin/Auswertungen so, wie sie eine andere Rolle sehen würde. Reine
// Oberflächen-Vorschau zum Prüfen von Sichtbarkeiten (z. B. „sieht eine
// Servicekraft die interne ID im Kundenprofil?") — ändert KEINE echten
// Rechte. Datenbank-Zugriffe laufen immer mit der echten Admin-Berechtigung;
// Aktionen, die für die vorgeschaute Rolle eigentlich verboten wären, werden
// dadurch nicht blockiert. Nicht für Sicherheitstests geeignet, nur für UI-Review.
export function ViewAsDropdown() {
  const role = useAuthStore((s) => s.role);
  const viewAsRole = useAuthStore((s) => s.viewAsRole);
  const setViewAsRole = useAuthStore((s) => s.setViewAsRole);
  // Bleibt immer für die ECHTE Rolle sichtbar, auch während eine Vorschau
  // läuft — sonst gäbe es über die Oberfläche keinen Weg zurück zur eigenen Ansicht.
  if (role !== 'admin') return null;
  return (
    <select
      value={viewAsRole ?? ''}
      onChange={(e) => setViewAsRole((e.target.value || null) as Role | null)}
      title="Ansicht als andere Rolle testen (nur Oberfläche — deine echten Rechte bleiben Admin)"
      style={selectStyle}
    >
      <option value="">Ansicht: Admin (echt)</option>
      {ROLE_OPTIONS.map((r) => <option key={r} value={r}>Ansicht: {ROLE_LABEL[r]}</option>)}
    </select>
  );
}

// Dauerhaft sichtbarer Hinweis, solange die Vorschau aktiv ist — damit nie
// unklar ist, dass gerade eine andere Rolle simuliert wird.
export function ViewAsBanner() {
  const role = useAuthStore((s) => s.role);
  const viewAsRole = useAuthStore((s) => s.viewAsRole);
  const setViewAsRole = useAuthStore((s) => s.setViewAsRole);
  if (role !== 'admin' || !viewAsRole) return null;
  return (
    <div role="status" style={bannerStyle}>
      <Eye size={16} style={{ flexShrink: 0 }} />
      <span style={{ flex: 1 }}>
        <strong>Vorschau aktiv:</strong> Du siehst das Callcenter gerade als{' '}
        <strong>{ROLE_LABEL[viewAsRole]}</strong> — deine echten Admin-Rechte bleiben bestehen.
      </span>
      <button onClick={() => setViewAsRole(null)} style={closeBtn}>
        <X size={14} /> Vorschau beenden
      </button>
    </div>
  );
}

const selectStyle: CSSProperties = {
  padding: '5px 8px', borderRadius: 6, border: '1px solid var(--border-color)',
  background: 'var(--surface)', fontSize: 12, cursor: 'pointer', color: 'inherit',
};

// Oben statt unten platziert — unten würde sich das Banner mit dem
// Power-Dialer (unten rechts, während eines aktiven Anrufs) überlappen.
// Liegt unter der ConnectionBanner (zIndex 9999): im seltenen Fall, dass
// beide gleichzeitig aktiv sind, hat die CRM-Verbindungswarnung Vorrang.
const bannerStyle: CSSProperties = {
  position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9990,
  display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px',
  background: '#1f2937', color: '#fff', fontSize: 13, boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
};

const closeBtn: CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 10px',
  borderRadius: 6, border: '1px solid rgba(255,255,255,0.4)', background: 'rgba(255,255,255,0.12)',
  color: '#fff', cursor: 'pointer', fontSize: 12, flexShrink: 0,
};
